import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mt-2 ml-5"
}
const _hoisted_2 = { class: "p-2 pl-0 pr-0 text-base" }
const _hoisted_3 = { class: "grid" }
const _hoisted_4 = { class: "col-fixed" }
const _hoisted_5 = { class: "col mt-1" }
const _hoisted_6 = ["textContent"]

import { computed, onMounted, ref, Ref, toRefs, watch, onBeforeUnmount } from 'vue';
import { RRule } from 'rrule';
import { useDateTime, useRequestCancel } from '@/composables';
import CalendarOverlappingMessage from './components/CalendarOverlappingMessage.vue';
import { CalendarService } from '@/services';
import { REQUEST, EMIT } from '@/enum';
import { useDebounceFn } from '@vueuse/core';

interface Props {
  eventId?: number;
  duration?: number;
  isRecurring: boolean;
  rrule: Partial<RRule>;
}

interface OverlappingEventInterface {
  start_at: string;
  end_at: string;
  date: string;
  time: string;
  event_id: number;
  paid: boolean;
  documented: boolean;
}

interface MessageInterface {
  main: string;
  sub: string;
  dateTime: { date: string; time: string }[];
}

enum ERROR {
  BILLED_MAIN = 'Billable Events can not overlap.',
  BILLED_SUB = 'Please choose a different time or adjust the conflicting event and try again.',
  NOT_DOCUMENTED_MAIN = 'Unable to create overlapping event.',
  NOT_DOCUMENTED_SUB = 'Please choose a different time or document the conflicting event and try again.',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarOverlappingEvents',
  props: {
    eventId: {},
    duration: {},
    isRecurring: { type: Boolean },
    rrule: {}
  },
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const { duration, eventId, isRecurring, rrule } = toRefs(props);

const overlaps: Ref<OverlappingEventInterface[]> = ref([]);

const showMore = ref(false);

const isLoading = ref(false);

const dateTime = useDateTime();

const requestCancel = useRequestCancel();

onMounted(() => queryOverlappingEvents());

const events = computed(() => (showMore.value ? overlaps.value : overlaps.value.slice(0, 4)));

const shouldShow = computed(() => events.value.length);

const shouldShowMore = computed(() => overlaps.value.length > 4);

const showLabel = computed(() => (showMore.value ? 'Show less' : `(${overlaps.value.length - 4}) more`));

const showLabelIcon = computed(() => (showMore.value ? 'expand_less' : 'expand_more'));

const messages = ref<MessageInterface[]>([
  { main: ERROR.BILLED_MAIN, sub: ERROR.BILLED_SUB, dateTime: [] },
  { main: ERROR.NOT_DOCUMENTED_MAIN, sub: ERROR.NOT_DOCUMENTED_SUB, dateTime: [] },
]);

const queryOverlappingEvents = async () => {
  if (!duration.value || duration.value <= 0) return;

  isLoading.value = true;
  const currentRRule = new RRule(rrule.value.options);
  currentRRule.origOptions.until = currentRRule.options.until;
  currentRRule.options.dtstart = new Date((currentRRule.options?.dtstart as Date).toUTCString());
  currentRRule.options.until = new Date((currentRRule.options?.until as Date).toUTCString());

  // count is not supported currently in the API
  currentRRule.options.count = null;
  currentRRule.options.byhour = [];
  currentRRule.options.byminute = [];
  currentRRule.options.bysecond = [];
  (currentRRule.options as RRule['options']).byweekday = (currentRRule.origOptions as RRule['options']).byweekday;
  (currentRRule.options as RRule['options']).bynweekday = null;
  const rrulestring = RRule.optionsToString((currentRRule as RRule).options);

  const payload = {
    start_at: dateTime.formatDateForApiWithoutConvertTimezone(currentRRule.options.dtstart, true),
    end_at: dateTime.formatDateForApiWithoutConvertTimezone(currentRRule.options.until, true),
    rrule: isRecurring.value ? rrulestring.split('\n')[1] : null,
    event_id: eventId.value || null,
    duration: duration.value,
  };

  const overlappingEvents = await CalendarService.getOverlappingEvents(payload);

  if (overlappingEvents) await defineOverlappingEvents(overlappingEvents.data.data);
  isLoading.value = false;
};
const debouncedQueryOverlappingEvents = useDebounceFn(async () => await queryOverlappingEvents(), 600, {
  maxWait: 2000,
});

watch(rrule, debouncedQueryOverlappingEvents, { deep: true });

const resetMessages = () => {
  messages.value[0].dateTime = [];
  messages.value[1].dateTime = [];
};

const defineOverlappingEvents = async (overlappingEvents: OverlappingEventInterface[]) => {
  overlaps.value = overlappingEvents;
  resetMessages();

  if (overlaps.value.length) {
    overlaps.value.forEach((event) => {
      if (event.documented && event.paid) {
        messages.value[0].dateTime.push({ date: event.date, time: event.time });
      } else if (!event.documented) {
        messages.value[1].dateTime.push({ date: event.date, time: event.time });
      }
    });
    emit(EMIT.ERROR);
  } else {
    emit(EMIT.OK);
  }
};

const onShowMore = () => (showMore.value = !showMore.value);

onBeforeUnmount(() => requestCancel.requestSaverAbort(REQUEST.OVERLAPPING_EVENTS));

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_Message = _resolveComponent("Message")!
  const _component_Skeleton = _resolveComponent("Skeleton")!

  return (shouldShow.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (!isLoading.value)
          ? (_openBlock(), _createBlock(_component_Message, {
              key: 0,
              severity: "error",
              icon: "null",
              closable: false
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      _createVNode(_component_Icon, {
                        name: "error",
                        class: "alert__icon"
                      })
                    ]),
                    _createElementVNode("div", _hoisted_5, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (message, index) => {
                        return (_openBlock(), _createElementBlock("div", { key: index }, [
                          (message.dateTime.length)
                            ? (_openBlock(), _createBlock(CalendarOverlappingMessage, {
                                key: 0,
                                message: message,
                                "style-class": index > 0 ? 'mt-3' : undefined
                              }, null, 8, ["message", "style-class"]))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    ]),
                    (shouldShowMore.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "text-sm font-semibold cursor-pointer",
                          onClick: onShowMore
                        }, [
                          _createElementVNode("span", {
                            class: "inline-block",
                            textContent: _toDisplayString(showLabel.value)
                          }, null, 8, _hoisted_6),
                          _createVNode(_component_Icon, {
                            name: showLabelIcon.value,
                            class: "relative",
                            style: {"top":"5px"}
                          }, null, 8, ["name"])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(_component_Skeleton, {
              key: 1,
              class: "w-full",
              height: "120px"
            }))
      ]))
    : _createCommentVNode("", true)
}
}

})